import Check from '@mui/icons-material/Check';
import Close from '@mui/icons-material/Close';
import DeleteOutline from '@mui/icons-material/DeleteOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Refresh from '@mui/icons-material/Refresh';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { type FC, useState } from 'react';
import { NAVY, RADIUS_CARD, SURFACE_CONTAINER_LOW, SURFACE_CONTAINER_LOWEST } from 'styles/tokens';
import textToReactNodes from 'utils/textToReactNodes';

import type { PlaygroundTurn, TurnRole } from '../types';
import CodeEditor from './CodeEditor';
import ToolCallCard from './ToolCallCard';

const ROLE_LABELS: Record<TurnRole, string> = {
  system: 'Sistema',
  user: 'Usuario',
  assistant: 'Asistente',
  tool: 'Tool',
};

const ROLE_OPTIONS: TurnRole[] = ['user', 'assistant', 'system', 'tool'];

interface TurnBubbleProps {
  turn: PlaygroundTurn;
  index: number;
  onUpdate: (patch: Partial<PlaygroundTurn>) => void;
  onDelete: () => void;
  onRegenerate?: () => void;
  regenerating?: boolean;
}

const TurnBubble: FC<TurnBubbleProps> = ({ turn, index, onUpdate, onDelete, onRegenerate, regenerating }) => {
  const [editing, setEditing] = useState(false);
  const [draftContent, setDraftContent] = useState(turn.content);
  const [draftRole, setDraftRole] = useState<TurnRole>(turn.role);

  const isUser = turn.role === 'user';
  const toolCalls = turn.toolCalls ?? [];

  const startEdit = () => {
    setDraftContent(turn.content);
    setDraftRole(turn.role);
    setEditing(true);
  };

  const save = () => {
    onUpdate({ content: draftContent, role: draftRole });
    setEditing(false);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isUser ? 'flex-end' : 'flex-start',
        '&:hover .turn-actions': { opacity: 1 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.25, px: 0.5 }}>
        <Typography
          sx={{
            fontFamily: 'Jost, sans-serif',
            fontSize: 10.5,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: alpha(NAVY, 0.45),
          }}
        >
          {ROLE_LABELS[turn.role]}
        </Typography>
        <Box className="turn-actions" sx={{ display: 'flex', gap: 0.25, opacity: 0, transition: 'opacity 0.15s ease' }}>
          {!editing && (
            <Tooltip title="Editar">
              <IconButton size="small" onClick={startEdit} sx={{ p: 0.25, color: alpha(NAVY, 0.5) }}>
                <EditOutlined sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!editing && onRegenerate && turn.role === 'assistant' && (
            <Tooltip title="Regenerar (re-ejecuta el modelo hasta este turno)">
              <IconButton
                size="small"
                onClick={onRegenerate}
                disabled={regenerating}
                sx={{ p: 0.25, color: alpha(NAVY, 0.5) }}
              >
                <Refresh sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {!editing && (
            <Tooltip title="Eliminar turno">
              <IconButton size="small" onClick={onDelete} sx={{ p: 0.25, color: alpha(NAVY, 0.5) }}>
                <DeleteOutline sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          maxWidth: '88%',
          minWidth: editing ? '60%' : undefined,
          bgcolor: isUser ? NAVY : SURFACE_CONTAINER_LOWEST,
          color: isUser ? '#fff' : NAVY,
          borderRadius: RADIUS_CARD,
          p: 1.25,
        }}
      >
        {editing ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Select
              value={draftRole}
              onChange={(e) => setDraftRole(e.target.value as TurnRole)}
              size="small"
              sx={{
                alignSelf: 'flex-start',
                bgcolor: SURFACE_CONTAINER_LOW,
                borderRadius: 1,
                fontFamily: 'Jost, sans-serif',
                fontSize: 12,
                color: NAVY,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
              }}
            >
              {ROLE_OPTIONS.map((role) => (
                <MenuItem key={role} value={role} sx={{ fontSize: 12, fontFamily: 'Jost, sans-serif' }}>
                  {ROLE_LABELS[role]}
                </MenuItem>
              ))}
            </Select>
            <CodeEditor
              value={draftContent}
              onChange={setDraftContent}
              monospace={false}
              minRows={2}
              ariaLabel={`Editar turno ${index + 1}`}
            />
            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
              <IconButton size="small" onClick={() => setEditing(false)} sx={{ color: alpha(NAVY, 0.5) }}>
                <Close sx={{ fontSize: 16 }} />
              </IconButton>
              <IconButton size="small" onClick={save} sx={{ color: NAVY }}>
                <Check sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          </Box>
        ) : (
          <>
            {turn.content.trim() ? (
              <Typography
                component="div"
                sx={{
                  fontFamily: 'Jost, sans-serif',
                  fontSize: 13.5,
                  lineHeight: 1.5,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {textToReactNodes(turn.content)}
              </Typography>
            ) : (
              toolCalls.length === 0 && (
                <Typography sx={{ fontSize: 12.5, fontStyle: 'italic', opacity: 0.5, fontFamily: 'Jost, sans-serif' }}>
                  (vacío)
                </Typography>
              )
            )}
            {toolCalls.map((tc) => (
              <ToolCallCard key={tc.id} toolCall={tc} />
            ))}
            {turn.toolCallId && (
              <Typography
                sx={{ mt: 0.5, fontSize: 10.5, fontFamily: 'ui-monospace, monospace', color: alpha(NAVY, 0.45) }}
              >
                ↳ {turn.toolCallId}
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default TurnBubble;
